import qs from 'qs'
import { dispatch, store } from '@/redux/store'
import { toast } from '@/modules/base/components/toast/toastConfig'
import {
  setAnalysisActive,
  setAnalysisDone,
  closeAvailableOcrModal,
  openAvailableOcrModal,
  fetchDocumentDetails,
} from '@/redux/ducks/document/drawer'
import { patchDocumentListItemMeta } from '@/redux/ducks/document/list'
import lang from '@/lang'
import checkOcrValues from '../_helpers/checkOcrValues'
import documentConfig from '@/modules/document/config'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import {
  errorCodes,
  experimentalFeatures,
  subscriptionLimits,
  subscriptionLimitationNames,
  subscriptionTypes,
  RECEIPT_TYPES,
} from '@/modules/base/util/constants'
import { UNSAFE_openUpgradeModal as openUpgradeModal } from '@/hooks/useUpgradeModal'
import { UNSAFE_openModal as openModal } from '@/modules/_helpers/openModal'
import { isMeinBuero } from '@/utils/build-env'

const isMeinbuero = isMeinBuero()
const { MAX_REACHED } = errorCodes

// FLAG:useDocuAi — re-added polling so a still-running DocuAI analysis result is applied once
// it finishes, instead of being evaluated once and dropped (ported from ralf/dms-xmlhandling).
const ANALYSIS_POLL_INTERVAL_MS = 3000
const ANALYSIS_MAX_POLL_ATTEMPTS = 20

// FLAG:useDocuAi — whole days until the re-analysis allowance resets. Deliberately calculated in
// UTC: the server counts usage from the start of the UTC month, and a locally calculated month
// boundary would be off by hours in our time zone on the first day — the message would then
// promise a day on which re-analysis is still blocked.
export const daysUntilQuotaReset = (now: Date = new Date()): number => {
  const nextMonthStartUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  return Math.max(1, Math.ceil((nextMonthStartUtc - now.getTime()) / (24 * 60 * 60 * 1000)))
}

// FLAG:useDocuAi — copy for the DMS's named refusal. Every refusal MUST have a sentence: otherwise
// the click is a placebo, which is exactly how „Erneut analysieren" already failed twice (first as
// a silent no-op, then, after processing attempts were exhausted, as a tile that spun endlessly
// until a watchdog stopped it — issue 04c0203b).
// An UNKNOWN reason falls back to `notPossible`: a new server reason must never produce a silent
// click in an older UI.
export const refusalMessage = (reason?: string | null): string => {
  switch (reason) {
    case 'quotaExceeded':
      return lang.documents.refreshAnalysis.quotaExceeded(daysUntilQuotaReset())
    case 'analysisPending':
      return lang.documents.refreshAnalysis.analysisPending
    case 'indexAttemptsExhausted':
      return lang.documents.refreshAnalysis.indexAttemptsExhausted
    default:
      return lang.documents.refreshAnalysis.notPossible
  }
}

interface OcrData {
    analysisFailed?: boolean
    isRunning?: boolean
    error?: string
    deliveryDate?: string
    date?: string
    autoscan?: string
    priceTotal?: number
    receiptNumber?: string
    paymentDueDate?: string
    payedAt?: string
    payee?: string
    payeeIban?: string
    payeeBic?: string
}

interface AnalyzeData {
    fileId: string
    ocrData?: OcrData
    props?: {
        data: {
            date?: string
            deliveryDate?: string
        },
        type?: string
    }
}

interface AnalyzeCallbacks {
    onStart: () => void
    onSuccess: () => void
    onError: (error: Error) => void
    onApplyData: (newData: unknown) => void
    onResult: (ocrData: OcrData, dateFor: string) => Promise<void>
}

interface AnalyzeOptions {
    autoAnalyze?: boolean
    skipModal?: boolean
    isUnlimitedOCRScans?: boolean
    customLang?: {
        title?: string
        confirmMessage?: (used: number, limit: number) => string
        no?: string
        yes?: string
    }
    suppressAddressModal?: boolean
    retryFailed?: boolean
}

interface ConfirmAnalyzeParams {
    data: AnalyzeData
    callbacks: AnalyzeCallbacks
    options?: AnalyzeOptions
}

export interface AnalysisOptions {
    skipModal?: boolean
    suppressAddressModal?: boolean
    isReanalyze?: boolean
    retryFailed?: boolean
}

export class DocumentAnalysisService {
  static async analyze(options: AnalysisOptions = {}): Promise<void> {
    const { document } = store.getState().document.drawer
    const { skipModal = false, isReanalyze = false, retryFailed = false } = options

    if (!document) {
      throw new Error('No document available for analysis')
    }

    // FLAG:useDocuAi — the DMS is the authority on whether a failed analysis may be
    // retried (metaData.ocr.retryAllowed, persisted from the contract envelope); the
    // local markers are only accelerators. analysisTimedOut counts as failed here:
    // a watchdog-cleaned tile means the terminal event was LOST, so analysisFailed
    // never got set — polling without retryFailed then makes the DMS stand down
    // forever (the L1 dead end, plan docuai-retry-and-result-recovery T7).
    const looksFailed = document.metaData?.analysisFailed === true
      || document.metaData?.analysisTimedOut === true
      || document.metaData?.ocr?.retryAllowed === true
    const isFailedRetry = looksFailed && (isReanalyze || retryFailed)
    if (isFailedRetry && document.metaData?.ocr?.retryAllowed === false) {
      store.dispatch(setAnalysisActive(false))
      toast.showToast({ type: 'warning', message: lang.documents.invoiceAnalysisRetryExhausted })
      return
    }

    if (this.hasAllValuesOverWritten()) {
      return
    }

    // A failed analysis has no reviewable data to offer — the "use existing OCR data"
    // modal must not swallow the retry (the failed doc's ocr object is bookkeeping:
    // retryAllowed / the stopped placeholder, not a result).
    if (!isFailedRetry && isReanalyze && document.metaData?.ocr && !document.metaData.ocr.isRunning) {
      store.dispatch(openAvailableOcrModal())
      return
    }

    const data: AnalyzeData = {
      fileId: document.id,
      props: { data: {} },
    }

    const callbacks: AnalyzeCallbacks = {
      onStart: () => store.dispatch(setAnalysisActive(true)),
      onResult: () => this.applyAnalysisResult(),
      onSuccess: () => store.dispatch(setAnalysisDone(true)),
      onApplyData: () => store.dispatch(closeAvailableOcrModal()),
      onError: (error) => {
        console.error('Analysis error:', error)
        store.dispatch(setAnalysisActive(false))
        toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
      },
    }

    const analysisOptions: AnalyzeOptions = {
      skipModal,
      isUnlimitedOCRScans: this.hasUnlimitedOCRScans(),
      retryFailed: isFailedRetry,
    }

    return this.confirmAnalyze({ data, callbacks, options: analysisOptions })
  }

  // FLAG:useDocuAi — user-triggered pull-forward of DocuAI's scheduled TRANSIENT retry
  // (plan docuai-analysis-retrying, "Jetzt erneut versuchen"). Deliberately NOT the
  // sanctioned-retry path: no scan modal, no SCANS counter bump, no polling loop — the
  // DMS only nudges the EXISTING DocuAI document (never /analyze, never quota) and the
  // next whisper or terminal event updates the tile on its own.
  static async retryNow(): Promise<void> {
    const { document } = store.getState().document.drawer
    if (!document) {
      throw new Error('No document available for retry-now')
    }
    try {
      const response = await request(`${documentConfig.analyzeFileUrl(document.id)}?${qs.stringify({ retryNow: true })}`, {
        auth: true,
        method: 'GET',
        isConcurrent: true,
      })
      const triggered = response?.body?.data?.analysisRetryTriggered === true
      toast.showToast(triggered
        ? { type: 'info', message: lang.documents.invoiceAnalysisRetryNowStarted }
        : { type: 'warning', message: lang.documents.invoiceAnalysisRetryNowNotPossible })
    } catch (error) {
      console.error('Retry-now error:', error)
      toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
    }
  }

  // FLAG:useDocuAi — „Als Rechnung analysieren". The door back for a document whose
  // analysis SUCCEEDED but recognised a non-invoice: DocuAI classified it as something
  // else (a Telekom "Mobilfunkreport" reads as `statement`), so no invoice schema ever
  // ran and every field is empty. The user corrects the document type by hand; this re-runs the
  // analysis with that document type asserted, so the correction actually binds.
  //
  // Deliberately NOT the `analyze()` path: that one is the legacy pipeline plus the
  // scan modal and the ocr-data modal, neither of which applies here (there is no
  // previous invoice data to offer, and flag-ON the legacy pipeline is not what runs).
  // Deliberately NOT `retryNow()` either: that nudges an already-scheduled retry and
  // never starts anything. This one DOES start a real DocuAI run and costs accordingly
  // — the DMS caps it at one per document Version.
  static async reanalyzeAsInvoice(): Promise<boolean> {
    const { document } = store.getState().document.drawer
    if (!document) {
      throw new Error('No document available for re-analysis')
    }
    try {
      const response = await request(
        `${documentConfig.analyzeFileUrl(document.id)}?${qs.stringify({ reanalyzeAs: 'invoice' })}`,
        { auth: true, method: 'GET', isConcurrent: true },
      )
      // The DMS answers TRIGGERED/IN_PROGRESS as isRunning — that IS the acceptance.
      // Anything else means a guard refused (already a recognised invoice, or the
      // one re-analysis per Version is spent); say so instead of implying a run.
      const started = response?.body?.data?.isRunning === true
      toast.showToast(started
        ? { type: 'info', message: lang.documents.reanalyzeAsInvoice.started }
        : { type: 'warning', message: lang.documents.reanalyzeAsInvoice.notPossible })
      return started
    } catch (error) {
      console.error('Re-analysis error:', error)
      toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
      return false
    }
  }

  // FLAG:useDocuAi — „Erneut analysieren". A GENUINELY new DocuAI run for a document whose
  // analysis has already successfully identified an invoice — the one state for which there
  // was no path before: `analyze()` is flag-OFF-only (legacy pipeline), `retryFailed` requires
  // an error, `reanalyzeAsInvoice()` requires "no invoice detected", and reset only
  // re-derives from the stored old result.
  //
  // It overwrites NOTHING: the result is stored in the analysis while the review remains
  // unchanged (the server-side F3 guard). It is explicitly applied via
  // „In Prüfung zurücksetzen" — hence the confirmation dialog says so as well.
  //
  // The refusal is NAMED rather than swallowed: the DMS limits this monthly per tenant, and
  // users must be told when the quota is exhausted; otherwise the click is a placebo — exactly
  // the bug that `retryFailed` had once before.
  static async refreshAnalysis(documentOverride?: { id?: string | number }): Promise<boolean> {
    // Drawer actions keep using the selected drawer document. Receipt detail and review hosts
    // already own the exact file they display and pass it explicitly; tying those surfaces to an
    // unrelated (usually closed) drawer would either throw or refresh the wrong document.
    const document = documentOverride ?? store.getState().document.drawer.document
    if (document?.id == null) {
      throw new Error('No document available for re-analysis')
    }
    try {
      const response = await request(
        `${documentConfig.analyzeFileUrl(document.id)}?${qs.stringify({ refreshAnalysis: true })}`,
        { auth: true, method: 'GET', isConcurrent: true },
      )
      const data = response?.body?.data
      // `refreshAllowed === false` is the DMS's explicit refusal; `isRunning` is its acceptance.
      // Checking the two (rather than only isRunning) distinguishes “denied” from
      // “something went wrong” and permits the appropriate message.
      if (data?.refreshAllowed === false) {
        // FLAG:useDocuAi — a synchronous refusal with isRunning=false is terminal runtime truth.
        // Apply it immediately to the list: a missed/late terminal /meta refresh may otherwise
        // leave `ocr.isRunning=true` behind the warning and keep the blue AI badge alive.
        // `analysisPending` deliberately reports isRunning=true and must keep the real run intact.
        if (data?.isRunning === false) {
          dispatch(patchDocumentListItemMeta(document.id, { analysisInProgress: false }))
        }
        toast.showToast({
          type: 'warning',
          message: refusalMessage(data?.refreshRefusedReason),
        })
        return false
      }
      const started = data?.isRunning === true
      toast.showToast(started
        ? { type: 'info', message: lang.documents.refreshAnalysis.started }
        : { type: 'warning', message: lang.documents.refreshAnalysis.notPossible })
      return started
    } catch (error) {
      console.error('Refresh-analysis error:', error)
      toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
      return false
    }
  }

  static async applyAnalysisResult(): Promise<void> {
    const { document } = store.getState().document.drawer
    if (!document) {
      throw new Error('No document available for applying analysis result')
    }

    const currentKind = document?.tags?.find(({ isSystem }) => !!isSystem)
    dispatch(
      fetchDocumentDetails({
        documentId: document.id,
        analyze: false,
        rename: false,
        analysisDone: true,
        preserveTags: currentKind ? [currentKind] : [],
      }),
    )
    store.dispatch(setAnalysisActive(false))
    store.dispatch(setAnalysisDone(true))
  }

  private static hasUnlimitedOCRScans(): boolean {
    const { subscriptions, selectedSubscriptionId } = store.getState().subscription.details
    const currentSubscriptionType = subscriptions.find((sub) => sub.id === selectedSubscriptionId)?.type
    return [subscriptionTypes.DOCUMENT_L, subscriptionTypes.DOCUMENT_XL].includes(currentSubscriptionType)
  }

  private static hasAllValuesOverWritten(): boolean {
    const { document } = store.getState().document.drawer
    if (!document) return false

    const ocrState = checkOcrValues(document?.expense, document?.revenue, document)
    if (ocrState?.hasValidValues && !ocrState?.hasUncheckedOcrValues && !ocrState?.hasEmptyValues) {
      toast.showToast({
        message: lang.documents.ocrAllValuesOverwritten,
        type: 'info',
      })
      store.dispatch(setAnalysisActive(false))
      store.dispatch(setAnalysisDone(true))
      return true
    }
    return false
  }

  static async confirmAnalyze(params: ConfirmAnalyzeParams): Promise<void> {
    const { data, callbacks, options = {} } = params
    const { fileId, ocrData, props = { data: {} } } = data
    const {
      onStart = () => {},
      onSuccess = () => {},
      onApplyData = () => {},
      onResult = async() => {},
      onError = () => {},
    } = callbacks
    const { autoAnalyze, isUnlimitedOCRScans, customLang = {}, retryFailed = false }: AnalyzeOptions = options
    let { skipModal } = options

    const scanFeature = user?.subscriptionData?.activeDetails?.find(
      (item) => item.name === subscriptionLimitationNames.SCANS,
    )
    const currentSubscriptionType = user?.subscriptionData?.subscriptions?.find((sub) =>
      [subscriptionTypes.DOCUMENT_L, subscriptionTypes.DOCUMENT_XL].includes(sub.type),
    )?.type
    if (isMeinbuero && (isUnlimitedOCRScans || currentSubscriptionType)) {
      skipModal = true
    }

    if (!scanFeature) {
      if (autoAnalyze) return
      openUpgradeModal({
        subscriptionChange: true,
      })
      return
    }

    const { used, limit } = scanFeature

    if (skipModal) {
      onStart()
      this.analyzeFile(fileId, onSuccess, onApplyData, props, onResult, onError, 0, retryFailed)
      return
    }

    openModal(close => ({
      component: 'ConfirmModal',
      props: {
        title: !ocrData
          ? (customLang.title ?? lang.file.analyze.confirmTitle)
          : lang.documents.applyDataModal.title,
        description: !ocrData
          ? ((customLang.confirmMessage && customLang.confirmMessage(used, limit))
                      ?? lang.file.analyze.confirmMessage(used, limit))
          : lang.documents.applyDataModal.description,
        abortButtonLabel: !ocrData
          ? (customLang.no ?? lang.generics.no)
          : lang.documents.applyDataModal.confirmNo,
        confirmButtonLabel: !ocrData
          ? (customLang.yes ?? lang.generics.yes)
          : lang.documents.applyDataModal.cancelAndScanAgain,
        disableBackdropClick: false,
        onConfirm: () => {
          close()
          onStart()
          this.analyzeFile(fileId, onSuccess, onApplyData, props, onResult, onError, 0, retryFailed)
        },
        onAbort: () => {
          if (ocrData) {
            const { deliveryDate, date } = ocrData
            const dateFor = deliveryDate || date

            onResult(ocrData, dateFor).then(onApplyData)
          }
          close()
        },
      },
    }))
  }

  private static showAnalyzeLimitModal(): void {
    return openUpgradeModal({
      limitReached: subscriptionLimits.DOCUMENT_SCAN_COUNT,
      forceType: true,
      subscriptionType: 'document',
    })
  }

  private static async analyzeFile(
    fileId: string,
    onSuccess: () => void,
    onApplyData: (data: unknown) => void,
    props: AnalyzeData['props'],
    onResult: (ocrData: OcrData, dateFor: string) => Promise<void>,
    onError: (error: Error) => void,
    pollAttempt = 0,
    retryFailed = false,
  ): Promise<void> {
    try {
      const query = {
        ...([RECEIPT_TYPES.EXPENSE, RECEIPT_TYPES.REVENUE].includes(props?.type) ? { type: props?.type } : {}),
        ...(retryFailed ? { retryFailed: true } : {}),
      }
      const queryParams = Object.keys(query).length ? qs.stringify(query) : undefined
      const response = await request(`${documentConfig.analyzeFileUrl(fileId)}${queryParams ? `?${queryParams}` : ''}`, {
        auth: true,
        method: 'GET',
        isConcurrent: true,
      })

      const {
        body: { data: analyzedData },
      } = response

      // FLAG:useDocuAi — the DMS says here that this trigger will produce nothing
      // (`indexAttemptsExhausted`: the indexing circuit breaker discards every further run for this
      // version). This applies to EVERY entry point that uses this shared path — „KI-Analyse
      // wiederholen", the legacy scan, the QR payment — and without this branch the user saw exactly
      // what this entry point was meant to eliminate: the click was accepted, the tile spun, and only
      // the watchdog stopped it minutes later.
      //
      // BEFORE the usage counter and polling loop: nothing is running, so no scan may be charged and no
      // result may be awaited.
      // Additive and inert with the flag OFF — the field exists only on the DocuAI contract path.
      if (analyzedData?.refreshRefusedReason) {
        toast.showToast({
          type: 'warning',
          message: refusalMessage(analyzedData.refreshRefusedReason),
        })
        onSuccess()
        return
      }

      const subscriptionDetails = user.subscriptionData?.activeDetails?.find(
        (item) => item.name === subscriptionLimitationNames.SCANS,
      )
      if (subscriptionDetails) {
        subscriptionDetails.used = subscriptionDetails.used + 1
      }

      if (!analyzedData?.isRunning && !analyzedData?.error && !analyzedData?.analysisFailed) {
        const {
          data: { date, deliveryDate },
        } = props
        const dateFor = deliveryDate || date

        await onResult(analyzedData, dateFor)
        onApplyData(analyzedData)
      }
      if (analyzedData?.error) {
        toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
      }
      if (analyzedData?.analysisFailed) {
        toast.showToast({
          type: 'warning',
          message: analyzedData.retryAllowed === false
            ? lang.documents.invoiceAnalysisRetryExhausted
            : lang.documents.invoiceAnalysisFailed,
        })
      }

      // FLAG:useDocuAi — analysis still running: poll until DocuAI finishes, then apply (ralf/dms-xmlhandling).
      // Gated on the tenant flag: isRunning is not a DocuAI-only state (the NATIF baseline knows it too),
      // and flag-OFF must keep the baseline behaviour of doing nothing while a scan is running (no re-request
      // storm, no SCANS-counter increments) — it then falls through to the unchanged final onSuccess guard.
      const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
      if (useDocuAiEnabled && analyzedData?.isRunning && !analyzedData?.error) {
        if (pollAttempt >= ANALYSIS_MAX_POLL_ATTEMPTS) {
          toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
          onSuccess()
          return
        }

        setTimeout(() => {
          this.analyzeFile(fileId, onSuccess, onApplyData, props, onResult, onError, pollAttempt + 1, false)
        }, ANALYSIS_POLL_INTERVAL_MS)
        return
      }

      if (!analyzedData?.isRunning || analyzedData?.error || analyzedData?.analysisFailed) {
        onSuccess()
      }
    } catch (error) {
      const errorData = error?.body?.meta

      if (errorData?.['limitation.documentScanCount']?.[0].code === MAX_REACHED) {
        this.showAnalyzeLimitModal()
      } else if (errorData?.xmlMapper?.[0].code === 'error') {
        toast.showToast({ type: 'error', message: lang.file.analyze.xmlError })
      } else if (errorData?.xml?.[0]?.code === errorCodes.NOT_SUPPORTED) {
        toast.showToast({
          type: 'error',
          message: lang.receipt.multiUpload.errors.embeddedXmlOrXmlNotSupported,
        })
      } else {
        toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage })
      }
      onError(error)
    }
  }
}
